import printJobQueue from '../indexedDB/printJobQueue';
import offlineDB from '../indexedDB/offlineDB';
import { isDefaultMevPrinter } from '../../mev/common/printer-constants';

export type PrintJobType = 'receipt' | 'kitchen' | 'session' | 'temporary_bill' | 'cart_items' | 'sales_report';
export type PrintJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface PrintJob {
  id?: number;
  orderId?: string;
  sessionId?: string;
  jobType: PrintJobType;
  orderData?: any;
  sessionData?: any;
  cartItems?: any[]; // Legacy field name
  cartItemsData?: any[]; // Preferred field name for cart items
  reportData?: any; // Sales report data
  stationStartTime?: Date; // For sales report
  endTime?: Date; // For sales report
  storeData: any;
  printerQuery: {
    api_key?: string;
    print_qr_receipt?: boolean;
  };
  // Printer configuration for cart_items jobs (from splitItemsByPrinter)
  isKitchenSlip?: boolean;
  kitchenSlipWithPrice?: boolean;
  cutbyItem?: boolean;
  cutbyCategory?: boolean;
  isTableOrder?: boolean;
  seatTableName?: string;
  tableMarkPlacement?: string;
  tableNote?: string;
  waiterSlip?: boolean;
  waiterSlipCut?: string;
  cupLabel?: boolean;
  cupLabelPaper?: string;
  bagLabel?: boolean;
  deliverySlip?: boolean;
  isDeliveryOrder?: boolean;
  guestName?: string;
  orderNumber?: string;
  kitchenTitle?: string;
  counter?: string;
  uberSlip?: {
    title?: string;
    orderNumber?: string;
    customerNumber?: string;
    customerName?: string;
  };
  deliveryAddress?: unknown;
  status?: PrintJobStatus;
  createdAt?: number;
  processedAt?: number;
  retries?: number;
  error?: string;
}

const MEV_API_KEYS = new Set(['MEV-RECEIPT-PRINTER', 'MEV-CUSTOMER-RECEIPT']);

async function printingStopped(job: { printerQuery?: { api_key?: string }; storeData?: { id?: string } }): Promise<boolean> {
  const apiKey = String(job.printerQuery?.api_key || '').trim();
  if (!apiKey || MEV_API_KEYS.has(apiKey)) return false;
  const storeId = String(job.storeData?.id || '').trim();
  if (!storeId) return false;
  try {
    const store = await offlineDB.getMasterData('stores', storeId);
    const list = Array.isArray(store?.mevPrinters) ? store.mevPrinters : [];
    const matches = list.filter((printer) => String(printer?.api_key || '').trim() === apiKey && !isDefaultMevPrinter(printer));
    return matches.length > 0 && matches.every((printer) => printer?.disabled === true);
  } catch {
    return false;
  }
}

/**
 * Print Job Manager
 * Manages print job queue operations
 */
class PrintJobManager {
  /**
   * Add a print job to the queue
   */
  async addPrintJob(job: Omit<PrintJob, 'id' | 'status' | 'createdAt' | 'retries'>): Promise<number> {
    try {
      if (await printingStopped(job)) return 0;
      const jobId = await printJobQueue.addPrintJob({
        ...job,
        status: 'pending',
        createdAt: Date.now(),
        retries: 0,
      });
      console.log(`[REPEAT DEBUG] [PrintJobManager] Added print job ${jobId} to queue:`, job.jobType);
      return jobId;
    } catch (error) {
      console.error('[REPEAT DEBUG] [PrintJobManager] Error adding print job:', error);
      throw error;
    }
  }

  /**
   * Get all pending print jobs
   */
  async getPendingJobs(): Promise<PrintJob[]> {
    try {
      const jobs = await printJobQueue.getPendingJobs();
      // Sort by createdAt (oldest first)
      const sorted = jobs.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      if (sorted.length > 0) {
        console.log(`[REPEAT DEBUG] [PrintJobManager] getPendingJobs returned ${sorted.length} job(s)`);
      }
      return sorted;
    } catch (error) {
      console.error('[REPEAT DEBUG] [PrintJobManager] Error getting pending jobs:', error);
      return [];
    }
  }

  /**
   * Get a print job by ID
   */
  async getPrintJob(id: number): Promise<PrintJob | null> {
    try {
      return await printJobQueue.getPrintJob(id);
    } catch (error) {
      console.error('[PRINTER DEBUG] [PrintJobManager] Error getting print job:', error);
      return null;
    }
  }

  /**
   * Mark a print job as completed
   */
  async markJobCompleted(id: number): Promise<boolean> {
    try {
      // Verify current status before marking as completed
      const jobBefore = await printJobQueue.getPrintJob(id);
      console.log(`[REPEAT DEBUG] [PrintJobManager] Marking job ${id} as completed. Current status: ${jobBefore?.status}`);
      
      const success = await printJobQueue.markJobCompleted(id);
      
      if (success) {
        // Verify status after update
        const jobAfter = await printJobQueue.getPrintJob(id);
        console.log(`[REPEAT DEBUG] [PrintJobManager] Marked job ${id} as completed. New status: ${jobAfter?.status}`);
        
        if (jobAfter?.status !== 'completed') {
          console.error(`[REPEAT DEBUG] [PrintJobManager] ERROR: Job ${id} status is ${jobAfter?.status}, expected 'completed'!`);
        }
      } else {
        console.log(`[REPEAT DEBUG] [PrintJobManager] Job ${id} was not in processing status, cannot mark as completed`);
      }
      
      return success;
    } catch (error) {
      console.error('[REPEAT DEBUG] [PrintJobManager] Error marking job as completed:', error);
      throw error;
    }
  }

  /**
   * Mark a print job as failed
   */
  async markJobFailed(id: number, error: string, retry: boolean = true): Promise<void> {
    try {
      await printJobQueue.markJobFailed(id, error, retry);
      console.log(`[PRINTER DEBUG] [PrintJobManager] Marked job ${id} as failed:`, error);
    } catch (error) {
      console.error('[PRINTER DEBUG] [PrintJobManager] Error marking job as failed:', error);
      throw error;
    }
  }

  /**
   * Mark a print job as processing
   * Returns true if successfully marked, false if job was already processing/completed
   */
  async markJobProcessing(id: number): Promise<boolean> {
    try {
      // Verify current status before marking as processing
      const jobBefore = await printJobQueue.getPrintJob(id);
      console.log(`[REPEAT DEBUG] [PrintJobManager] Attempting to mark job ${id} as processing. Current status: ${jobBefore?.status}`);
      
      const success = await printJobQueue.markJobProcessing(id);
      
      if (success) {
        // Verify status after update
        const jobAfter = await printJobQueue.getPrintJob(id);
        console.log(`[REPEAT DEBUG] [PrintJobManager] Successfully marked job ${id} as processing. New status: ${jobAfter?.status}`);
        
        if (jobAfter?.status !== 'processing') {
          console.error(`[REPEAT DEBUG] [PrintJobManager] ERROR: Job ${id} status is ${jobAfter?.status}, expected 'processing'!`);
        }
      } else {
        console.log(`[REPEAT DEBUG] [PrintJobManager] Job ${id} was already ${jobBefore?.status}, cannot mark as processing`);
      }
      return success;
    } catch (error) {
      console.error('[REPEAT DEBUG] [PrintJobManager] Error marking job as processing:', error);
      throw error;
    }
  }

  /**
   * Get retryable failed jobs
   */
  async getRetryableJobs(): Promise<PrintJob[]> {
    try {
      return await printJobQueue.getRetryableJobs();
    } catch (error) {
      console.error('[PRINTER DEBUG] [PrintJobManager] Error getting retryable jobs:', error);
      return [];
    }
  }

  /**
   * Delete a print job
   */
  async deletePrintJob(id: number): Promise<void> {
    try {
      await printJobQueue.deletePrintJob(id);
      console.log(`[PRINTER DEBUG] [PrintJobManager] Deleted job ${id}`);
    } catch (error) {
      console.error('[PRINTER DEBUG] [PrintJobManager] Error deleting print job:', error);
      throw error;
    }
  }

  /**
   * Cleanup old completed jobs
   */
  async cleanupOldJobs(daysOld: number = 7): Promise<void> {
    try {
      await printJobQueue.cleanupOldJobs(daysOld);
      console.log(`[PRINTER DEBUG] [PrintJobManager] Cleaned up jobs older than ${daysOld} days`);
    } catch (error) {
      console.error('[PRINTER DEBUG] [PrintJobManager] Error cleaning up old jobs:', error);
    }
  }

  /**
   * Get all jobs (for debugging)
   */
  async getAllJobs(): Promise<PrintJob[]> {
    try {
      return await printJobQueue.getAllJobs();
    } catch (error) {
      console.error('[PRINTER DEBUG] [PrintJobManager] Error getting all jobs:', error);
      return [];
    }
  }
}

// Singleton instance
const printJobManager = new PrintJobManager();

export default printJobManager;

